import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import styled from '@emotion/styled';
import * as yup from 'yup';
import UDText from '../../../../ud-ui/components/text';
import UDFormInput from '../../../../ud-form/components/input';
import BorderedInput from '../bordered-input';
import UDButton from '../../../../ud-ui/components/button';
import { AppDispatch } from '../../../../../store/store';
import { upsertChampionshipPlayerApplication } from '../../../store/championshipTeams/actions';
import { ApplicationStatus } from '../../../../applications/domain/enums/ApplicationStatus';
import { birthDateFormatter } from '../../../../ud-form/components/input/formatters/birthDateFormatter';

const CreateTeamPlayerFormWrapper = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
`;

const CreateTeamPlayerFormContainer = styled.div`
  padding: 16px 20px 24px 20px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 0 1px rgba(26, 26, 41, 0.08), 0 8px 16px rgba(26, 26, 41, 0.08);
  margin-bottom: 42px;
`;

const CreateTeamPlayerFormFields = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  
  > div {
    width: 100%;
    margin: 0 5px;
    &:first-child {
      margin-left: 0;
    }
  }
`;

const teamPlayerValidator = yup
  .object()
  .shape({
    firstName: yup.string().required(),
    lastName: yup.string().required(),
    middleName: yup.string().optional(),
    birthDate: yup.string().matches(/^(\d{2})\.(\d{2})\.(\d{4})$/).required(),
  });

type CreateTeamPlayerFormProps = {
  teamId: number;
  championshipId: number;
  existPlayerIds: number[];
};


const CreateTeamPlayerForm = ({ teamId, championshipId }: CreateTeamPlayerFormProps) => {
  const { t } = useTranslation();

  const dispatch: AppDispatch = useDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleSubmit = useCallback((values, { resetForm }) => {
    setIsLoading(true);

    dispatch(upsertChampionshipPlayerApplication({
      teamId,
      championshipId,
      userApplication: {
        createUser: {
          name: values.firstName,
          surname: values.lastName,
          middleName: values.middleName,
          birthDate: values.birthDate,
        }
      },
      state: ApplicationStatus.PENDING,
    })).then(() => {
      resetForm({
        values: {
          firstName: '',
          lastName: '',
          middleName: '',
          birthDate: '',
        },
      });
    }).finally(() => {
      setIsLoading(false);
    });
  }, [championshipId, dispatch, teamId]);

  return (
    <CreateTeamPlayerFormWrapper>
      <Formik
        initialValues={{}}
        validationSchema={teamPlayerValidator}
        onSubmit={handleSubmit}
      >
        {(formProps) => (
          <form onSubmit={formProps.handleSubmit}>
            <CreateTeamPlayerFormContainer>
              <UDText type="subhead" className="bold">
                {t('applications.addPlayerFormTitle')}
              </UDText>

              <CreateTeamPlayerFormFields>
                <UDFormInput
                  name="lastName"
                  placeholder="Фамилия"
                  inputComponent={BorderedInput}
                />
                <UDFormInput
                  name="firstName"
                  placeholder="Имя"
                  inputComponent={BorderedInput}
                />
                <UDFormInput
                  name="middleName"
                  placeholder="Отчество"
                  inputComponent={BorderedInput}
                />
                <UDFormInput
                  name="birthDate"
                  placeholder="Дата рождения"
                  mask="дд.мм.гггг"
                  formatter={birthDateFormatter}
                  inputComponent={BorderedInput}
                />
                <UDButton
                  className="ml-4"
                  type="submit"
                  variant="primary"
                  disabled={isLoading}
                  loading={isLoading}
                >{t('applications.addPlayerButtonTitle')}</UDButton>
              </CreateTeamPlayerFormFields>
            </CreateTeamPlayerFormContainer>
          </form>
        )}
      </Formik>
    </CreateTeamPlayerFormWrapper>
  );
};

export  default CreateTeamPlayerForm;
